import type { FC, MouseEvent, ReactNode } from "react";
import React, { useCallback, useId, useState } from "react";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { type IconName, Icons } from "@/components/NavIcon";

// TODO: allow item to specify an `href` and use an <a/> element for links.

export type Item = {
  id: string;
  label: ReactNode;
  icon?: IconName;
  onClick: (handleClose: () => void, id: string) => void;
};

type DropdownMenuProps = {
  /** Text label. Displayed in the button if no `buttonElement` provided. */
  buttonLabel: string;
  /** An element to display in the button. */
  buttonElement?: ReactNode;
  /** A list of items to be displayed in the menu */
  items: Item[];
  testId?: string;
};

/**
 * Renders items in a Dropdown menu.
 *
 * Delegates much of the implementation to MUI Menu
 * @see https://mui.com/material-ui/react-menu/
 */
const DropdownMenu: FC<DropdownMenuProps> = ({
  buttonLabel,
  buttonElement,
  items,
  testId = "dropdown-menu",
}) => {
  const buttonId = useId();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  return (
    <nav data-testid={testId}>
      <IconButton
        color="inherit"
        id={buttonId}
        aria-haspopup="true"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-label={buttonLabel}
        onClick={handleClick}
      >
        {buttonElement ?? buttonLabel}
      </IconButton>

      <Menu
        id={menuId}
        sx={{ top: 5 }}
        aria-labelledby={buttonId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {items.map(({ id, icon, label, onClick }) => {
          const Icon = icon ? Icons[icon] : null;
          return (
            <MenuItem
              key={id}
              sx={{ minWidth: 200, padding: 1 }}
              onClick={() => {
                onClick(handleClose, id);
              }}
            >
              {Icon && (
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
              )}
              <ListItemText sx={{ fontSize: 12 }}>{label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </nav>
  );
};

export default DropdownMenu;
